import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  distinct,
  Position,
  PositionIdPatch,
  PositionPatch,
  leadingGrouping,
  moveGroupingPatches,
  moveIntoGroupingPatches,
  reorderPatches,
} from "../types/position";
import { PositionKind, isSumKind } from "../types/position-kind";
import { PositionUnitSelectItems } from "../types/position-unit";
import { Separator } from "@/shared/components/ui/separator";
import { Button } from "@/shared/components/ui/button";
import {
  ChevronRight,
  CornerDownRight,
  EllipsisVertical,
  Percent,
  Plus,
  Settings2,
  Trash2,
} from "lucide-react";
import t from "@/lang/lang";
import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { cn } from "cn";
import { TextInputField } from "@/shared/components/field/input-field";
import {
  AddPositionBaseRequest,
  PatchPositionBaseRequest,
  RemovePositionBaseRequest,
  RemovePositionsByGroupingBaseRequest,
  RenameGroupingBaseRequest,
} from "../types/position-requests";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { useErrValidationFailedContext } from "@/shared/lib/errors/err-validation-failed-context";
import { RtkQueryMutation } from "@/shared/lib/debounce/debounce";
import { DocumentBase } from "@/services/backend/documents/types/base";
import { v4 } from "uuid";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { ServiceObjectReference } from "@/services/backend/documents/types/service-object-references-base";
import { ServiceObjectName } from "@/services/backend/customers/components/service-object-name";
import { ScrollArea, ScrollBar } from "@/shared/components/ui/scroll-area";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellCheckbox,
  ETableCellDecimalInput,
  ETableCellInput,
  ETableCellSelect,
  ETableCellTextarea,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { Input } from "@/shared/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import { PositionCategorySelectItems } from "../types/position-category";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  newPriceModifier,
  PriceModifier,
  PriceModifierKind,
  priceModifierAmount,
  PriceModifierSchema,
  withPriceModifierAmount,
  withPriceModifierKind,
  withPriceModifierSchema,
} from "../../pricemodifier/types/price-modifier";
import {
  DragNDropWrapper,
  ETableCellDragNDropGrip,
} from "@/shared/components/tables/e-table-cell-drag-n-drop";
import { DragEndEvent } from "@dnd-kit/core";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Spinner } from "@/shared/components/ui/spinner";
import { newSubPositions, PositionBaseData } from "../types/position-base-data";

// WARNING: PositionComponents is incomplete. It will work for
// the most part but may have bugs. Use PositionsTable for now.
//
// Known bugs / improvements:
// - consider grouping positions by tabValues first
//   This could save performance here.
//   No need to memoize. This will run on every change!
//   const tabsPositionsMap = Map.groupBy(positions, (p) => p[config.tabs]);
//   const tabValues = [...tabsPositionsMap.keys()]
//   how to handle zero tab then?
// - missing RemoveTabDialog
// - reset on error may not be happening in some mutations

/**
 * tabsType defines ...
 *
 * group: uses the `position.group`
 * area: uses the `position.area`
 *
 * The values match the field name of position.
 */
type tabsType = "group" | "area";

/**
 * See {@link tabsType}.
 */
type sectionsType = "group" | "area";

/**
 * TableColumnKey defines the available columns for the {@link PositionsTable}.
 * The values are the field names of positions.
 */
export const TableColumn = {
  DnDGrip: { key: "dndGrip", columnOrder: 0, title: "", className: "" },
  Number: { key: "number", columnOrder: 1, title: t("Nummer"), className: "" },
  Active: { key: "active", columnOrder: 2, title: t("Aktiv"), className: "" },
  ServiceObject: {
    key: "serviceObjectId",
    columnOrder: 3,
    title: t("Serviceobjekt"),
    className: "",
  },
  Group: { key: "group", columnOrder: 4, title: t("Gruppe"), className: "" },
  Area: { key: "area", columnOrder: 5, title: t("Bereich"), className: "" },
  Kind: { key: "kind", columnOrder: 6, title: t("Art"), className: "" },
  Category: {
    key: "category",
    columnOrder: 7,
    title: t("Kategorie"),
    className: "",
  },
  ProductNumber: {
    key: "productNumber",
    columnOrder: 8,
    title: t("Produktnummer"),
    className: "",
  },
  TextShort: {
    key: "textShort",
    columnOrder: 9,
    title: t("Text kurz"),
    className: "",
  },
  Text: { key: "text", columnOrder: 10, title: t("Text"), className: "w-full" },
  Cycle: {
    key: "cycle",
    columnOrder: 11,
    title: t("Turnus (Wo.)"),
    className: "",
  },
  Hours: { key: "hours", columnOrder: 12, title: t("Stunden"), className: "" },
  HoursRate: {
    key: "hoursRate",
    columnOrder: 13,
    title: t("Stundensatz"),
    className: "",
  },
  Count: { key: "count", columnOrder: 14, title: t("Anzahl"), className: "" },
  Unit: { key: "unit", columnOrder: 15, title: t("Einheit"), className: "" },
  UnitPrice: {
    key: "unitPrice",
    columnOrder: 16,
    title: t("Preis / Einheit"),
    className: "",
  },
  BasePrice: {
    key: "basePrice",
    columnOrder: 17,
    title: t("Preis"),
    className: "",
  },
  PriceModifiers: {
    key: "priceModifiers",
    columnOrder: 18,
    title: t("Rabatte"),
    className: "",
  },
  FinalPrice: {
    key: "finalPrice",
    columnOrder: 19,
    title: t("Endpreis"),
    className: "",
  },
  SubscriptionInterval: {
    key: "subscriptionInterval",
    columnOrder: 20,
    title: t("Preis-Intervall"),
    className: "",
  },
  SubscriptionPrice: {
    key: "subscriptionPrice",
    columnOrder: 21,
    title: t("Preis"),
    className: "",
  },
  PriceUsed: {
    key: "priceUsed",
    columnOrder: 22,
    title: t("Preis im Dokument"),
    className: "",
  },
  Actions: { key: "tableActions", columnOrder: 23, title: "", className: "" },
  // satisfies makes sure we only define Position field names as values
  // + dnd & options
} as const satisfies Record<
  string,
  {
    key: keyof Position | "dndGrip" | "tableActions";
    /**
     * columnOrder sets in which order the columns are rendered.
     * This prevents awkward rendering with rows spanning other
     * columns / needing more space (e.g. text).
     */
    columnOrder: number;
    title: string;
    className?: string;
  }
>;

export type TableColumn = (typeof TableColumn)[keyof typeof TableColumn];

const TableColumsList = Object.values(TableColumn);

const defaultTableColumns: TableColumn[] = [
  TableColumn.DnDGrip,
  TableColumn.Number,
  TableColumn.Active,
  TableColumn.Text,
  TableColumn.Category,
  TableColumn.Hours,
  TableColumn.HoursRate,
  TableColumn.Count,
  TableColumn.Unit,
  TableColumn.UnitPrice,
  TableColumn.FinalPrice,
  TableColumn.Actions,
];

const tabsTriggerClass = "h-8";

type PositionComponentContextType = Omit<
  PositionsComponentConfig,
  "tableColumns" // handled via columns
> & {
  document: DocumentBase;
  columns: TableColumn[];
  // selectedColumns are the columns chosen by the user, columns those of
  // them which are shown
  selectedColumns: TableColumn[];
  setColumns: (columns: TableColumn[]) => void;
  addPositionOptions: PositionBaseData[];
  addPositionMutation: RtkQueryMutation<AddPositionBaseRequest, DocumentBase>;
  patchPositionMutation: RtkQueryMutation<
    PatchPositionBaseRequest,
    DocumentBase
  >;
  onPositionsPatch: (patches: PositionIdPatch[]) => void;
  removePositionMutation: RtkQueryMutation<
    RemovePositionBaseRequest,
    DocumentBase
  >;
  renameGroupingMutation: RtkQueryMutation<
    RenameGroupingBaseRequest,
    DocumentBase
  >;
  removePositionsByGroupingMutation: RtkQueryMutation<
    RemovePositionsByGroupingBaseRequest,
    DocumentBase
  >;
  disabled: boolean;
  fieldNamePrefix: string;
};

const PositionComponentContext = createContext<PositionComponentContextType>({
  // The default is never read: every consumer renders inside the provider.
  document: {} as DocumentBase,
  tabs: "group",
  tabsName: "Tab",
  tabsAllowEmpty: false,
  zeroTabActive: true,
  zeroTabName: "Übergreifend",
  zeroTabAddPositionOptions: [],
  sections: "area",
  sectionsName: "Abschnitt",
  sectionsAllowEmpty: false,
  columns: defaultTableColumns,
  selectedColumns: defaultTableColumns,
  setColumns: () => null,
  addPositionOptions: [],
  addPositionMutation: (() => null) as unknown as RtkQueryMutation<
    AddPositionBaseRequest,
    DocumentBase
  >,
  patchPositionMutation: (() => null) as unknown as RtkQueryMutation<
    PatchPositionBaseRequest,
    DocumentBase
  >,
  onPositionsPatch: () => null,
  removePositionMutation: (() => null) as unknown as RtkQueryMutation<
    RemovePositionBaseRequest,
    DocumentBase
  >,
  renameGroupingMutation: (() => null) as unknown as RtkQueryMutation<
    RenameGroupingBaseRequest,
    DocumentBase
  >,
  removePositionsByGroupingMutation: (() =>
    null) as unknown as RtkQueryMutation<
    RemovePositionsByGroupingBaseRequest,
    DocumentBase
  >,
  disabled: false,
  fieldNamePrefix: "",
});

const usePositionComponentContext = () => useContext(PositionComponentContext);

/**
 * usePositionsContextValue builds the value of the
 * PositionComponentContext from the config and the props of a component
 * providing it. The columns are state, so a component may change them.
 */
function usePositionsContextValue(
  config: PositionsComponentConfig,
  {
    document,
    addPositionMutation,
    patchPositionMutation,
    onPositionsPatch,
    removePositionMutation,
    renameGroupingMutation,
    removePositionsByGroupingMutation,
    disabled = false,
    fieldNamePrefix = "",
  }: Omit<PositionsComponentProps, "config" | "positions">,
): PositionComponentContextType {
  const [columns, setColumns] = useState<TableColumn[]>(config.tableColumns);

  return useMemo(
    () => ({
      ...config,
      document,
      // a position can only pick between several service objects
      columns: columns
        .filter(
          (column) =>
            column !== TableColumn.ServiceObject ||
            (document.serviceObjects?.length ?? 0) > 1,
        )
        .sort((a, b) => (a.columnOrder < b.columnOrder ? -1 : 1)),
      selectedColumns: columns,
      setColumns,
      addPositionMutation,
      patchPositionMutation,
      onPositionsPatch,
      removePositionMutation,
      renameGroupingMutation,
      removePositionsByGroupingMutation,
      disabled: disabled || isImmutable(document),
      fieldNamePrefix,
    }),
    [
      document,
      config,
      columns,
      setColumns,
      addPositionMutation,
      patchPositionMutation,
      onPositionsPatch,
      removePositionMutation,
      renameGroupingMutation,
      removePositionsByGroupingMutation,
      disabled,
      fieldNamePrefix,
    ],
  );
}

/**
 * PositionsComponentConfig allows to configure how the
 * PositionsComponent should behave. It should be configured
 * per service kind (or as needed).
 */
export type PositionsComponentConfig = {
  tabs: tabsType;
  tabsName: string;
  tabsAllowEmpty: boolean;
  zeroTabActive: boolean;
  zeroTabName: string;
  zeroTabAddPositionOptions: PositionBaseData[];
  sections: sectionsType;
  sectionsName: string;
  sectionsAllowEmpty: boolean;
  addPositionOptions: PositionBaseData[];
  // tableColumns are the columns shown. The service object column is
  // only shown while the document has more than one service object.
  tableColumns: TableColumn[];
  // editableNumbers lets users set the numbers of positions, for
  // documents which do not number them by rules.
  editableNumbers?: boolean;
};

export type PositionsComponentProps = {
  document: DocumentBase;
  config: PositionsComponentConfig;
  positions: Position[];
  addPositionMutation: RtkQueryMutation<AddPositionBaseRequest, DocumentBase>;
  patchPositionMutation: RtkQueryMutation<
    PatchPositionBaseRequest,
    DocumentBase
  >;
  /**
   * onPositionsPatch saves patches of several positions at once, e.g. the
   * orders after a drag and drop. The caller owns the optimistic update.
   */
  onPositionsPatch: (patches: PositionIdPatch[]) => void;
  removePositionMutation: RtkQueryMutation<
    RemovePositionBaseRequest,
    DocumentBase
  >;
  renameGroupingMutation: RtkQueryMutation<
    RenameGroupingBaseRequest,
    DocumentBase
  >;
  removePositionsByGroupingMutation: RtkQueryMutation<
    RemovePositionsByGroupingBaseRequest,
    DocumentBase
  >;
  /**
   * disabled makes the component read-only. It is always read-only for
   * an immutable document.
   */
  disabled?: boolean;
  /**
   * fieldNamePrefix prefixes the field names of the positions, the keys
   * of their field errors, for positions nested in the document, e.g.
   * "scopeOfWork.<id>." for "scopeOfWork.<id>.positions.<id>.text".
   */
  fieldNamePrefix?: string;
};

/**
 *
 */
export function PositionsComponent({
  config,
  positions,
  ...props
}: PositionsComponentProps) {
  // the tabs to be rendered, state, and the default select if any
  const tabValues = distinct(positions, config.tabs, config.tabsAllowEmpty);

  const [tabValue, setTabValue] = useState(tabValues[0]);

  const contextValue = usePositionsContextValue(config, props);

  return (
    <PositionComponentContext value={contextValue}>
      <div className="rounded-lg border shadow-sm">
        <Tabs value={tabValue} onValueChange={setTabValue} className="gap-0">
          <TabsList className="h-11 w-full rounded-b-none p-1.5">
            <ScrollArea className={cn("min-w-0 flex-1")}>
              <div className="flex w-max items-center">
                {tabValues.map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className={cn(tabsTriggerClass, "flex-none")}
                  >
                    {tab}
                  </TabsTrigger>
                ))}
              </div>
              <ScrollBar orientation="horizontal" hidden />
            </ScrollArea>
            <Separator
              orientation="vertical"
              className="mr-2 ml-2 bg-black/30"
            />
            <ZeroTabTrigger />
            <AddTabPopover />
          </TabsList>
          {tabValues.map((tab) => (
            <TabsContent value={tab} key={tab}>
              <TabContent tabValue={tab} positions={positions} />
            </TabsContent>
          ))}
          <ZeroTabContent
            positions={positions.filter((p) => p[config.tabs] === "")}
          />
        </Tabs>
      </div>
    </PositionComponentContext>
  );
}

function ZeroTabTrigger() {
  const { zeroTabActive, zeroTabName } = usePositionComponentContext();

  if (!zeroTabActive) {
    return null;
  }

  return (
    <TabsTrigger value="" className={cn(tabsTriggerClass, "max-w-min mr-2")}>
      {zeroTabName}
    </TabsTrigger>
  );
}

/**
 * ZeroTabContent allows to modify the behaviour of the UI
 * for positions stretching accross all tabs.
 *
 * There can still be sections.
 */
function ZeroTabContent({ positions }: { positions: Position[] }) {
  const { zeroTabActive } = usePositionComponentContext();

  if (!zeroTabActive) {
    return null;
  }

  return (
    <TabsContent value="">
      <TabContent tabValue={""} positions={positions} />
    </TabsContent>
  );
}

function AddTabPopover() {
  const {
    document,
    tabs,
    tabsName,
    addPositionMutation,
    disabled,
    fieldNamePrefix,
  } = usePositionComponentContext();
  const [open, setOpen] = useState(false);
  const [positionId, setPositionId] = useState(v4());
  // this is not the "text" of the position but
  // the name of the new group / area.
  const [name, setName] = useState("");

  const [post, { isLoading, reset }] = addPositionMutation;
  const { write } = useErrValidationFailedContext();

  const doAdd = () => {
    if (isLoading) return;

    post({
      id: document.id,
      positionId,
      kind: positionTitleKindFor(tabs),
      [tabs]: name,
    })
      .unwrap()
      .then(() => {
        setOpen(false);
        setName("");
        setPositionId(v4());
        reset();
      })
      .catch((e) => {
        write(e);
      });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled}>
          <Plus />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="grid gap-2" align="end">
        <TextInputField
          label={tabsName + " hinzufügen"}
          text={name}
          onTextChange={setName}
          className="h-8"
          placeholder={t("Name eingeben ...")}
          fieldName={`${fieldNamePrefix}positions.${positionId}.${tabs}`}
        />
        <Button
          size="sm"
          className="w-full"
          onClick={doAdd}
          disabled={isLoading}
        >
          {t("Hinzufügen")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function positionTitleKindFor(
  v: tabsType | sectionsType,
): typeof PositionKind.GroupTitle | typeof PositionKind.AreaTitle {
  return v == "group" ? PositionKind.GroupTitle : PositionKind.AreaTitle;
}

function TabContent({
  tabValue,
  positions,
}: {
  tabValue: string;
  positions: Position[];
}) {
  const { tabs, zeroTabActive, sections, sectionsAllowEmpty } =
    usePositionComponentContext();

  const tabPositions = useMemo(() => {
    // narrow to tab relevant positions and remove
    // the tab title position -> not rendered here
    return positions.filter(
      (pos) =>
        pos[tabs] === tabValue && pos.kind !== positionTitleKindFor(tabs),
    );
  }, [positions, tabs, tabValue]);

  const sectionValues = distinct(tabPositions, sections, sectionsAllowEmpty);

  return (
    <div>
      {zeroTabActive && tabValue !== "" && <TabHeader tabValue={tabValue} />}
      {sectionValues.map((sectionValue) => (
        <Section
          key={sectionValue}
          tabValue={tabValue}
          sectionValue={sectionValue}
          positions={tabPositions.filter((p) => p[sections] === sectionValue)}
        />
      ))}
      {zeroTabActive && tabValue === "" && (
        <Section
          tabValue={tabValue}
          sectionValue=""
          positions={tabPositions.filter((p) => p[sections] === "")}
        />
      )}
    </div>
  );
}

function TabHeader({ tabValue }: { tabValue: string }) {
  return (
    <div className="flex items-center rounded-none border-t border-b p-2">
      <div className="ml-auto">
        <AddSectionPopover tabValue={tabValue} />
      </div>
    </div>
  );
}

/**
 * AddSectionPopover adds a new section within this tab.
 *
 * This makes an implicit assumption about a hierarchy
 * between "group" and "area", which is fine, since this
 * whole component does.
 */
function AddSectionPopover({ tabValue }: { tabValue: string }) {
  const {
    document,
    tabs,
    sections,
    sectionsName: sectionName,
    addPositionMutation,
    disabled,
    fieldNamePrefix,
  } = usePositionComponentContext();
  const [open, setOpen] = useState(false);
  const [positionId, setPositionId] = useState(v4());
  // this is not the "text" of the position but
  // the name of the new group / area.
  const [name, setName] = useState("");

  const [post, { isLoading, reset }] = addPositionMutation;
  const { write } = useErrValidationFailedContext();

  const doAdd = () => {
    if (isLoading) return;

    post({
      id: document.id,
      positionId,
      kind: positionTitleKindFor(sections),
      // add to group and area at the same time
      [sections]: name,
      [tabs]: tabValue,
    })
      .unwrap()
      .then(() => {
        setOpen(false);
        setName("");
        setPositionId(v4());
        reset();
      })
      .catch((e) => {
        write(e);
      });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabled}>
          {`${sectionName} hinzufügen`}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="grid gap-2" align="end">
        <TextInputField
          label={sectionName + " hinzufügen"}
          text={name}
          onTextChange={setName}
          className="h-8"
          placeholder={t("Name eingeben ...")}
          fieldName={`${fieldNamePrefix}positions.${positionId}.${sections}`}
        />
        <Button
          size="sm"
          className="w-full"
          onClick={doAdd}
          disabled={isLoading}
        >
          {t("Hinzufügen")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function Section({
  tabValue,
  sectionValue,
  positions,
}: {
  tabValue: string;
  sectionValue: string;
  positions: Position[];
}) {
  return (
    <Collapsible defaultOpen>
      <SectionHead
        tabValue={tabValue}
        sectionValue={sectionValue}
        positions={positions}
      />
      <CollapsibleContent>
        <PositionsTableInternal positions={positions} />
      </CollapsibleContent>
    </Collapsible>
  );
}

function SectionHead({
  tabValue,
  sectionValue,
}: {
  tabValue: string;
  sectionValue: string;
  positions: Position[];
}) {
  const { tabs, sections } = usePositionComponentContext();

  return (
    <div className="bg-muted text-muted-foreground flex items-center">
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="group">
          <ChevronRight className="group-data-[state=open]:rotate-90" />
        </Button>
      </CollapsibleTrigger>
      <GroupingNameInput
        grouping={
          {
            group: "",
            area: "",
            [tabs]: tabValue,
            [sections]: sectionValue,
          } as Grouping
        }
        field={sections}
        className="text-foreground max-w-max border-none font-semibold shadow-none md:text-base"
      />
      <div className="ml-auto flex items-center">
        <RemoveSectionDialog tabValue={tabValue} sectionValue={sectionValue} />
        <AddPositionDropdown tabValue={tabValue} sectionValue={sectionValue} />
      </div>
    </div>
  );
}

function newGroupingKey(v: tabsType | sectionsType): "newGroup" | "newArea" {
  return v == "group" ? "newGroup" : "newArea";
}

/** Grouping is the group and area a title or a position belongs to. */
type Grouping = { group: string; area: string };

/**
 * GroupingNameInput renames the field of the grouping, e.g. the area of
 * a room, across all its positions. The rename is saved on blur or
 * Enter, not per keystroke: every save moves the positions to the new
 * name, and intermediate names may clash with existing groupings.
 */
function GroupingNameInput({
  grouping,
  field,
  className,
}: {
  grouping: Grouping;
  field: tabsType | sectionsType;
  className?: string;
}) {
  const { document, disabled, renameGroupingMutation } =
    usePositionComponentContext();
  const [rename] = renameGroupingMutation;
  const { write } = useErrValidationFailedContext();
  const value = grouping[field];

  const [draft, setDraft] = useState(value);
  const [shownValue, setShownValue] = useState(value);
  if (shownValue !== value) {
    setShownValue(value);
    setDraft(value);
  }
  const skipCommit = useRef(false);

  const commit = () => {
    const name = draft.trim();
    if (skipCommit.current || name === "" || name === value) {
      skipCommit.current = false;
      setDraft(value);
      return;
    }
    rename({ id: document.id, ...grouping, [newGroupingKey(field)]: name })
      .unwrap()
      .catch((e) => {
        setDraft(value);
        toast(...parseRTKQueryErrorToSonner(e));
        write(e);
      });
  };

  return (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
        if (e.key === "Escape") {
          skipCommit.current = true;
          e.currentTarget.blur();
        }
      }}
      disabled={disabled || value === ""}
      className={className}
    />
  );
}

function RemoveSectionDialog({
  tabValue,
  sectionValue,
}: {
  tabValue: string;
  sectionValue: string;
}) {
  const {
    document,
    tabs,
    sections,
    sectionsName,
    disabled,
    removePositionsByGroupingMutation,
  } = usePositionComponentContext();
  const [open, setOpen] = useState(false);
  const [del, { isLoading, error, reset }] = removePositionsByGroupingMutation;

  const doDelete = () => {
    if (isLoading) return;

    del({ id: document.id, [tabs]: tabValue, [sections]: sectionValue })
      .unwrap()
      .then(() => {
        reset();
        setOpen(false);
      })
      .catch((e) => {
        toast(...parseRTKQueryErrorToSonner(e));
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" disabled={disabled}>
          <Trash2 />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${sectionsName} ${t("löschen?")}`}</DialogTitle>
          <DialogDescription>
            {`${t("Alle Positionen in")} ${sectionValue} ${t("werden unwiderruflich gelöscht.")}`}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doDelete}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * useAddPosition returns a function adding a position of base to the
 * grouping. The total belongs to no group or area.
 */
function useAddPosition(): (
  base: PositionBaseData,
  grouping: Partial<Grouping>,
) => void {
  const { document, addPositionMutation } = usePositionComponentContext();
  const [post] = addPositionMutation;

  return (base, grouping) => {
    post({
      id: document.id,
      positionId: v4(),
      ...(base.kind !== PositionKind.Total && grouping),
      ...base,
      subPositions: newSubPositions(base),
    })
      .unwrap()
      .catch((e) => {
        toast(...parseRTKQueryErrorToSonner(e, false));
      });
  };
}

function AddPositionDropdown({
  tabValue,
  sectionValue,
  parentId,
}: {
  tabValue: string;
  sectionValue: string;
  parentId?: UUID;
}) {
  const {
    tabs,
    zeroTabActive,
    zeroTabAddPositionOptions,
    sections,
    addPositionOptions,
    disabled,
  } = usePositionComponentContext();
  const addPosition = useAddPosition();
  const doAdd = (base: PositionBaseData) =>
    addPosition(base, { [tabs]: tabValue, [sections]: sectionValue });

  const options =
    zeroTabActive && tabValue === ""
      ? zeroTabAddPositionOptions
      : addPositionOptions;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" disabled={disabled}>
          <Plus />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuGroup>
          <DropdownMenuLabel>
            {parentId && t("Unter-")} {t("Position hinzufügen")}
          </DropdownMenuLabel>
          {options.map((option) => (
            <DropdownMenuItem
              key={option.label ?? option.text}
              onClick={() => doAdd(option)}
            >
              <Plus />
              {option.label ?? option.text}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PositionsTableInternal({ positions }: { positions: Position[] }) {
  const { columns, onPositionsPatch, disabled } = usePositionComponentContext();

  const doPatch = useDoPatchPosition();

  // positions are the rows of this section only, so a drag never moves a
  // position into another group or area.
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const patches = reorderPatches(
      positions,
      String(active.id),
      String(over.id),
    );
    if (patches.length === 0) {
      return;
    }
    onPositionsPatch(patches);
  };

  return (
    <DragNDropWrapper
      items={positions.map((p) => p.id)}
      handleDragEnd={handleDragEnd}
      disabled={disabled}
    >
      {/* the component draws the outer border, so the table omits its own */}
      <ETable className="border-none [&_th]:border-t-0 [&_tr>*:first-child]:border-l-0 [&_tr>*:last-child]:border-r-0">
        <ETableHeader>
          <ETableRow>
            {columns.map((column) =>
              column === TableColumn.DnDGrip ? (
                <ETableHead key={column.key} className="h-8 w-8 p-0">
                  <ColumnSettingsDropdown />
                </ETableHead>
              ) : (
                <ETableHead
                  key={column.key}
                  className={cn("h-8", column.className)}
                >
                  {column.title}
                </ETableHead>
              ),
            )}
          </ETableRow>
        </ETableHeader>
        <ETableBody>
          {positions.length === 0 && <TableEmptyRow colSpan={columns.length} />}
          {positions.map((position) => (
            <PositionRow
              key={position.id}
              position={position}
              doPatch={(patch) => doPatch(position.id, patch)}
            />
          ))}
        </ETableBody>
      </ETable>
    </DragNDropWrapper>
  );
}

/**
 * useDoPatchPosition returns a function saving a patch of one position,
 * reporting the persistence state and the errors of a failed save.
 */
function useDoPatchPosition(): (
  positionId: UUID,
  patch: PositionPatch,
) => void {
  const { document, patchPositionMutation } = usePositionComponentContext();
  const [patch] = patchPositionMutation;
  const { write } = useErrValidationFailedContext();
  const { setPersistenceState } = usePersistenceContext();
  return (positionId, positionPatch) => {
    setPersistenceState(PersistenceState.SAVING);
    patch({ id: document.id, positionId, ...positionPatch })
      .unwrap()
      .catch((e) => {
        toast(...parseRTKQueryErrorToSonner(e, false));
        write(e);
      })
      .finally(() => {
        setPersistenceState(PersistenceState.SAVED);
      });
  };
}

function PositionRow({
  position,
  doPatch,
}: {
  position: Position;
  doPatch: (r: PositionPatch) => void;
}) {
  const { columns } = usePositionComponentContext();

  switch (position.kind) {
    case PositionKind.AreaTitle:
    case PositionKind.GroupTitle:
      // either are rendered as tab or section
      return null;
    case PositionKind.Text:
      return <PositionRowText position={position} doPatch={doPatch} />;
    case PositionKind.SubTotal:
    case PositionKind.Total:
      return <PositionRowSum position={position} doPatch={doPatch} />;
    // these kinds use the default row
    case PositionKind.PriceModifierRelative:
    case PositionKind.Default:
    case PositionKind.Dynamic:
    default:
      return (
        <ETableRow>
          {columns.map((column) => (
            <TableCell
              key={column.key}
              column={column}
              position={position}
              doPatch={doPatch}
            />
          ))}
        </ETableRow>
      );
  }
}

function PositionRowSum({
  position,
  doPatch,
}: {
  position: Position;
  doPatch: (r: PositionPatch) => void;
}) {
  const { columns, disabled, fieldNamePrefix } = usePositionComponentContext();

  return (
    <ETableRow className="font-semibold">
      {position.kind === PositionKind.Total ? (
        // the total stays last and cannot be dragged
        <ETableCell aria-hidden="true" />
      ) : (
        <ETableCellDragNDropGrip id={position.id} disabled={disabled} />
      )}
      <TableCell
        column={TableColumn.Number}
        position={position}
        doPatch={doPatch}
      />
      <ETableCellInput
        colSpan={columns.length - 4}
        value={position.text}
        onChange={(e) => doPatch({ text: e.target.value })}
        fieldName={`${fieldNamePrefix}positions.${position.id}.text`}
        disabled={disabled}
      />
      <TableCell
        column={TableColumn.FinalPrice}
        position={position}
        doPatch={doPatch}
      />
      <TableCell
        column={TableColumn.Actions}
        position={position}
        doPatch={doPatch}
      />
    </ETableRow>
  );
}

function PositionRowText({
  position,
  doPatch,
}: {
  position: Position;
  doPatch: (r: PositionPatch) => void;
}) {
  const { columns, disabled } = usePositionComponentContext();

  // columns used in text rows, all others are ignored such that
  // text can span accross columns
  // Columns commented out are shown for reasoning
  const tableColumns = new Set<TableColumn>([
    TableColumn.DnDGrip,
    TableColumn.ServiceObject,
    TableColumn.Number,
    TableColumn.Text,
    TableColumn.Kind,
    TableColumn.Active,
    // TableColumn.Category, not shown since always empty
    // TableColumn.Cycle, not shown since not applicable
    TableColumn.Area,
    TableColumn.Group,
    // TableColumn.Hours, not shown since not applicable
    // TableColumn.HoursRate, not shown since not applicable
    // TableColumn.Count, not shown since not applicable
    // TableColumn.Unit, not shown since not applicable
    // TableColumn.UnitPrice, not shown since not applicable
    // TableColumn.BasePrice, not shown since not applicable
    // TableColumn.PriceModifiers, not shown since not applicable
    // TableColumn.FinalPrice, not shown since not applicable
    TableColumn.Actions,
  ]);
  const columnsUsed = columns.filter((c) => tableColumns.has(c));

  const colSpan = columns.length - columnsUsed.length + 1;

  return (
    <ETableRow>
      {columnsUsed.map((c) => {
        if (c == TableColumn.Text) {
          return (
            <ETableCellTextarea
              key={c.key}
              colSpan={colSpan}
              placeholder={t("Text eingeben ...")}
              value={position.text}
              onChange={(e) => doPatch({ text: e.target.value })}
              disabled={disabled}
            />
          );
        }
        return (
          <TableCell
            key={c.key}
            column={c}
            position={position}
            doPatch={doPatch}
          />
        );
      })}
    </ETableRow>
  );
}

function TableCell({
  column,
  position,
  doPatch,
  colSpan,
}: {
  column: TableColumn;
  position: Position;
  doPatch: (r: PositionPatch) => void;
  colSpan?: number;
}) {
  const { document, disabled, editableNumbers, fieldNamePrefix } =
    usePositionComponentContext();

  switch (column.key) {
    case "dndGrip":
      return <ETableCellDragNDropGrip id={position.id} disabled={disabled} />;
    case "number":
      // Always show indicator that row is a child.
      // Children do not get numbers by convention.
      // If needed this can be loosened.
      if (position.parentId) {
        return (
          <ETableCell colSpan={colSpan}>
            <CornerDownRight className="ml-2 size-5" />
          </ETableCell>
        );
      }
      return (
        <ETableCellInput
          key={column.key}
          className="w-20 font-semibold"
          value={position.number}
          onChange={(e) => doPatch({ number: e.target.value })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.number`}
          disabled={disabled}
          colSpan={colSpan}
          readOnly={!editableNumbers}
        />
      );
    case "serviceObjectId":
      return (
        <ETableCellSelect
          label={t("Serviceobjekt")}
          items={[
            { value: NullUUID, label: t("Kein Serviceobjekt") },
            ...(document.serviceObjects ?? []).map(
              (so: ServiceObjectReference) => ({
                value: so.serviceObjectId,
                label: <ServiceObjectName id={so.serviceObjectId} />,
              }),
            ),
          ]}
          value={position.serviceObjectId || NullUUID}
          onValueChange={(serviceObjectId) => doPatch({ serviceObjectId })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.serviceObjectId`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "productNumber":
      return (
        <ETableCellInput
          key={column.key}
          className="w-24"
          value={position.productNumber}
          onChange={(e) => doPatch({ productNumber: e.target.value })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.productNumber`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "textShort":
      return (
        <ETableCellInput
          key={column.key}
          className="w-48"
          value={position.textShort}
          onChange={(e) => doPatch({ textShort: e.target.value })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.textShort`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "text":
      return (
        <ETableCellInput
          key={column.key}
          className="w-full"
          value={position.text}
          onChange={(e) => doPatch({ text: e.target.value })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.text`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "kind":
      return (
        <ETableCell key={column.key} colSpan={colSpan}>
          {position[column.key]}
        </ETableCell>
      );
    case "active":
      return (
        <ETableCellCheckbox
          key={column.key}
          checked={position.active}
          onCheckedChange={(v) => doPatch({ active: v ? true : false })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.active`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "category":
      return (
        <ETableCellSelect
          label={t("Kategorie")}
          items={PositionCategorySelectItems}
          value={position.category}
          onValueChange={(category) => doPatch({ category })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.category`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "area":
      // For now: change through delete + new only. Change if needed.
      // Make sure to consider tab/section - area/group matching.
      return (
        <ETableCell key={column.key} colSpan={colSpan}>
          {position[column.key]}
        </ETableCell>
      );
    case "group":
      // For now: change through delete + new only. Change if needed.
      // Make sure to consider tab/section - area/group matching.
      return (
        <ETableCell key={column.key} colSpan={colSpan}>
          {position[column.key]}
        </ETableCell>
      );
    case "cycle":
      // a price modifier is not repeated on its own
      if (position.kind === PositionKind.PriceModifierAbsolute)
        return <BlindCell />;
      if (position.kind === PositionKind.PriceModifierRelative)
        return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.cycle}
          onValueChange={(cycle) => doPatch({ cycle })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.cycle`}
          disabled={disabled}
          precision={0}
          colSpan={colSpan}
        />
      );
    case "hours":
      if (position.kind === PositionKind.Dynamic) return <BlindCell />;
      if (position.kind === PositionKind.PriceModifierAbsolute)
        return <BlindCell />;
      if (position.kind === PositionKind.PriceModifierRelative)
        return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.hours}
          onValueChange={(hours) => doPatch({ hours })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.hours`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "hoursRate":
      if (position.kind === PositionKind.Dynamic) return <BlindCell />;
      if (position.kind === PositionKind.PriceModifierAbsolute)
        return <BlindCell />;
      if (position.kind === PositionKind.PriceModifierRelative)
        return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.hoursRate}
          onValueChange={(hoursRate) => doPatch({ hoursRate })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.hoursRate`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "count":
      if (position.kind === PositionKind.Dynamic) return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.count}
          onValueChange={(count) => doPatch({ count })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.count`}
          disabled={disabled}
          precision={0}
          colSpan={colSpan}
        />
      );
    case "unit":
      return (
        <ETableCellSelect
          label={t("Einheit")}
          items={PositionUnitSelectItems}
          value={position.unit}
          onValueChange={(unit) => doPatch({ unit })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.unit`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "unitPrice":
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.unitPrice}
          onValueChange={(unitPrice) => doPatch({ unitPrice })}
          fieldName={`${fieldNamePrefix}positions.${position.id}.unitPrice`}
          disabled={disabled}
          colSpan={colSpan}
        />
      );
    case "basePrice":
      if (position.kind === PositionKind.Dynamic) return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.basePrice}
          onValueChange={() => null}
          precision={2}
          fieldName={`${fieldNamePrefix}positions.${position.id}.basePrice`}
          readOnly
          colSpan={colSpan}
        />
      );
    case "priceModifiers":
      return (
        <ETableCell className="p-0">
          <PriceModifierDialog
            position={position}
            onPriceModifierChange={(priceModifiers) =>
              doPatch({ priceModifiers })
            }
            trigger={<Button variant="ghost">{t("Rabatte")}</Button>}
          />
        </ETableCell>
      );
    case "finalPrice":
      if (position.kind === PositionKind.Dynamic) return <BlindCell />;
      return (
        <ETableCellDecimalInput
          key={column.key}
          value={position.finalPrice}
          onValueChange={() => null}
          precision={2}
          fieldName={`${fieldNamePrefix}positions.${position.id}.finalPrice`}
          readOnly
          colSpan={colSpan}
        />
      );
    case "tableActions":
      return <PositionActions position={position} doPatch={doPatch} />;
  }
}

function BlindCell() {
  return (
    <ETableCell className="bg-[repeating-linear-gradient(-45deg,var(--muted),var(--muted)_2px,transparent_2px,transparent_4px)]" />
  );
}

function PositionActions({
  position,
  doPatch,
}: {
  position: Position;
  doPatch: (r: PositionPatch) => void;
}) {
  const { disabled } = usePositionComponentContext();

  return (
    <ETableCell className="w-8 p-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button variant="ghost" className="w-8 rounded-none">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuLabel>{t("Aktionen")}</DropdownMenuLabel>
            <RemovePositionDialog position={position} />
            {/* sums are always top-level and carry no modifiers, as do
                positions of a modifier kind */}
            {!isSumKind(position.kind) && (
              <AddChildDropdownItem position={position} />
            )}
            {position.kind !== PositionKind.PriceModifierRelative &&
              position.kind !== PositionKind.PriceModifierAbsolute &&
              !isSumKind(position.kind) && (
                <PriceModifierDialog
                  position={position}
                  onPriceModifierChange={(priceModifiers) =>
                    doPatch({ priceModifiers })
                  }
                />
              )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </ETableCell>
  );
}

function RemovePositionDialog({ position }: { position: Position }) {
  const { document, removePositionMutation } = usePositionComponentContext();
  const [open, setOpen] = useState(false);
  const [del, { isLoading, error, reset }] = removePositionMutation;

  const doDelete = () => {
    if (isLoading) return;

    del({ id: document.id, positionId: position.id })
      .unwrap()
      .then(() => {
        reset();
        setOpen(false);
      })
      .catch((e) => {
        toast(...parseRTKQueryErrorToSonner(e));
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setOpen(!open);
          }}
        >
          <Trash2 />
          {t("Löschen")}
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Position löschen?")}</DialogTitle>
          <DialogDescription>
            {t("Die Position wird unwiederruflich gelöscht.")}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doDelete}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}

function AddChildDropdownItem({ position }: { position: Position }) {
  // For now we limit child positions to simple text positions.
  // This can be extended if needed.
  const { document, addPositionMutation } = usePositionComponentContext();

  const [post, { isLoading }] = addPositionMutation;

  // only one level nesting allowed
  if (position.parentId) return;

  const doAdd = () => {
    if (isLoading) return;

    post({
      id: document.id,
      positionId: v4(),
      parentId: position.id,
      kind: PositionKind.Text,
    });
  };

  return (
    <DropdownMenuItem onSelect={doAdd}>
      {isLoading ? <Spinner /> : <Plus />}
      {t("Text hinzufügen")}
    </DropdownMenuItem>
  );
}

const priceModifierKindSelectItems = [
  { value: PriceModifierKind.Discount, label: t("Rabatt") },
  { value: PriceModifierKind.Surcharge, label: t("Zuschlag") },
];

const priceModifierSchemaSelectItems = [
  { value: PriceModifierSchema.Relative, label: "%" },
  { value: PriceModifierSchema.Absolute, label: "€" },
];

function PriceModifierDialog({
  position,
  onPriceModifierChange,
  trigger,
}: {
  position: Position;
  onPriceModifierChange: (modifiers: PriceModifier[]) => void;
  trigger?: ReactNode;
}) {
  const { disabled, fieldNamePrefix } = usePositionComponentContext();
  const [open, setOpen] = useState(false);

  const modifiers = position.priceModifiers;
  const change = (i: number, modifier: PriceModifier) =>
    onPriceModifierChange(modifiers.map((m, j) => (j === i ? modifier : m)));
  const remove = (i: number) =>
    onPriceModifierChange(modifiers.filter((_, j) => j !== i));
  const add = () => onPriceModifierChange([...modifiers, newPriceModifier()]);
  const fieldName = (i: number, field: string) =>
    `${fieldNamePrefix}positions.${position.id}.priceModifiers.${i}.${field}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setOpen(!open);
            }}
          >
            <Percent />
            {t("Rabatte / Zuschläge")}
          </DropdownMenuItem>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t("Rabatte für Position:")} {position.number}
          </DialogTitle>
        </DialogHeader>
        <ETable>
          <ETableHeader>
            <ETableRow>
              <ETableHead>{t("Aktiv")}</ETableHead>
              <ETableHead className="w-full">{t("Bezeichnung")}</ETableHead>
              <ETableHead>{t("Art")}</ETableHead>
              <ETableHead>{t("Berechnung")}</ETableHead>
              <ETableHead>{t("Betrag")}</ETableHead>
              <ETableHead>{t("Wert")}</ETableHead>
              <ETableHead />
            </ETableRow>
          </ETableHeader>
          <ETableBody>
            {modifiers.length === 0 && <TableEmptyRow colSpan={7} />}
            {modifiers.map((modifier, i) => (
              // modifiers have no identity of their own, their index is
              // their key in the backend as well
              <ETableRow key={i}>
                <ETableCellCheckbox
                  checked={modifier.active}
                  onCheckedChange={(active) =>
                    change(i, { ...modifier, active: active === true })
                  }
                  fieldName={fieldName(i, "active")}
                  disabled={disabled}
                />
                <ETableCellInput
                  value={modifier.title}
                  onChange={(e) =>
                    change(i, { ...modifier, title: e.target.value })
                  }
                  fieldName={fieldName(i, "title")}
                  disabled={disabled}
                />
                <ETableCellSelect
                  label={t("Art")}
                  items={priceModifierKindSelectItems}
                  value={modifier.kind}
                  onValueChange={(kind) =>
                    change(
                      i,
                      withPriceModifierKind(
                        modifier,
                        kind as PriceModifierKind,
                      ),
                    )
                  }
                  fieldName={fieldName(i, "kind")}
                  disabled={disabled}
                />
                <ETableCellSelect
                  label={t("Berechnung")}
                  items={priceModifierSchemaSelectItems}
                  value={modifier.schema}
                  onValueChange={(schema) =>
                    change(
                      i,
                      withPriceModifierSchema(
                        modifier,
                        schema as PriceModifierSchema,
                      ),
                    )
                  }
                  fieldName={fieldName(i, "schema")}
                  disabled={disabled}
                />
                <ETableCellDecimalInput
                  value={priceModifierAmount(modifier)}
                  onValueChange={(amount) =>
                    change(i, withPriceModifierAmount(modifier, amount))
                  }
                  precision={2}
                  fieldName={fieldName(
                    i,
                    modifier.schema === PriceModifierSchema.Relative
                      ? "relativeRate"
                      : "value",
                  )}
                  disabled={disabled}
                />
                <ETableCellDecimalInput
                  value={modifier.value}
                  onValueChange={() => null}
                  precision={2}
                  readOnly
                />
                <ETableCell className="w-8 p-0">
                  <Button
                    variant="ghost"
                    className="w-8 rounded-none"
                    aria-label={t("Entfernen")}
                    title={t("Entfernen")}
                    onClick={() => remove(i)}
                    disabled={disabled}
                  >
                    <Trash2 />
                  </Button>
                </ETableCell>
              </ETableRow>
            ))}
          </ETableBody>
        </ETable>
        {!disabled && (
          <Button variant="outline" size="sm" className="w-fit" onClick={add}>
            <Plus />
            {t("Rabatt hinzufügen")}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * PositionsTableConfig configures a {@link PositionsTable}. groupName and
 * areaName name the groups and areas, e.g. "Gebäude" and "Raum".
 */
export type PositionsTableConfig = {
  groupName: string;
  areaName: string;
  addPositionOptions: PositionBaseData[];
  tableColumns: TableColumn[];
  editableNumbers?: boolean;
};

export type PositionsTableProps = Omit<PositionsComponentProps, "config"> & {
  config: PositionsTableConfig;
};

/**
 * PositionsTable renders the positions as one table in their order, like
 * the positions table of the PDF: the titles of groups and areas are rows
 * heading the positions within them. Whichever of group and area is used
 * first leads, the other is added within it, see {@link leadingGrouping}.
 *
 * Positions, groups and areas are added from the header, positions and
 * the other grouping within a title from its row. A position dragged to
 * another place takes over the group and area of that place.
 */
export function PositionsTable({
  config,
  positions,
  ...props
}: PositionsTableProps) {
  const componentConfig = useMemo<PositionsComponentConfig>(
    () => ({
      tabs: "group",
      tabsName: config.groupName,
      tabsAllowEmpty: true,
      zeroTabActive: false,
      zeroTabName: "",
      zeroTabAddPositionOptions: config.addPositionOptions,
      sections: "area",
      sectionsName: config.areaName,
      sectionsAllowEmpty: true,
      addPositionOptions: config.addPositionOptions,
      tableColumns: config.tableColumns,
      editableNumbers: config.editableNumbers,
    }),
    [config],
  );
  const contextValue = usePositionsContextValue(componentConfig, props);

  return (
    <PositionComponentContext value={contextValue}>
      <div className="rounded-lg border shadow-sm">
        <GroupedPositionsTable positions={positions} />
      </div>
    </PositionComponentContext>
  );
}

function isTitleKind(kind: PositionKind): boolean {
  return kind === PositionKind.GroupTitle || kind === PositionKind.AreaTitle;
}

function titleField(kind: PositionKind): "group" | "area" {
  return kind === PositionKind.GroupTitle ? "group" : "area";
}

function GroupedPositionsTable({ positions }: { positions: Position[] }) {
  const { columns, onPositionsPatch, disabled } = usePositionComponentContext();
  const doPatch = useDoPatchPosition();

  const sorted = useMemo(
    () => [...positions].sort((a, b) => a.order - b.order),
    [positions],
  );
  const leader = leadingGrouping(positions);
  // the groupings added at the top: the leading one, or both while
  // neither is used
  const topFields: ("group" | "area")[] = leader ? [leader] : ["group", "area"];

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const dragged = positions.find((p) => p.id === active.id);
    // a title moves its whole grouping
    const patches = (
      dragged && isTitleKind(dragged.kind)
        ? moveGroupingPatches
        : moveIntoGroupingPatches
    )(positions, String(active.id), String(over.id));
    if (patches.length === 0) {
      return;
    }
    onPositionsPatch(patches);
  };

  return (
    <DragNDropWrapper
      items={sorted.map((p) => p.id)}
      handleDragEnd={handleDragEnd}
      disabled={disabled}
    >
      {/* the wrapper draws the outer border, so the table omits its own */}
      <ETable className="border-none [&_th]:border-t-0 [&_tr>*:first-child]:border-l-0 [&_tr>*:last-child]:border-r-0">
        <ETableHeader>
          <ETableRow>
            {columns.map((column) =>
              column === TableColumn.DnDGrip ? (
                <ETableHead key={column.key} className="h-8 w-8 p-0">
                  <ColumnSettingsDropdown />
                </ETableHead>
              ) : column === TableColumn.Actions ? (
                <ETableHead key={column.key} className="h-8 w-8 p-0">
                  <GroupingActionsDropdown
                    grouping={{ group: "", area: "" }}
                    fields={topFields}
                  />
                </ETableHead>
              ) : (
                <ETableHead
                  key={column.key}
                  className={cn("h-8", column.className)}
                >
                  {column.title}
                </ETableHead>
              ),
            )}
          </ETableRow>
        </ETableHeader>
        <ETableBody>
          {sorted.length === 0 && <EmptyPositionsRow fields={topFields} />}
          {sorted.map((position) =>
            isTitleKind(position.kind) ? (
              <PositionRowTitle
                key={position.id}
                position={position}
                leading={titleField(position.kind) === leader}
                doPatch={(patch) => doPatch(position.id, patch)}
              />
            ) : (
              <PositionRow
                key={position.id}
                position={position}
                doPatch={(patch) => doPatch(position.id, patch)}
              />
            ),
          )}
        </ETableBody>
      </ETable>
    </DragNDropWrapper>
  );
}

/**
 * PositionRowTitle is the row of a group or area title. The leading one
 * is written in upper case, like in the PDF.
 */
function PositionRowTitle({
  position,
  leading,
  doPatch,
}: {
  position: Position;
  leading: boolean;
  doPatch: (r: PositionPatch) => void;
}) {
  const { columns, disabled } = usePositionComponentContext();
  const field = titleField(position.kind);
  const grouping = { group: position.group, area: position.area };

  return (
    <ETableRow
      className={cn("font-semibold", leading ? "bg-muted" : "bg-muted/50")}
    >
      <ETableCellDragNDropGrip id={position.id} disabled={disabled} />
      <TableCell
        column={TableColumn.Number}
        position={position}
        doPatch={doPatch}
      />
      <ETableCell colSpan={columns.length - 3} className="p-0">
        <GroupingNameInput
          grouping={grouping}
          field={field}
          className={cn(
            "rounded-none border-none bg-transparent font-semibold shadow-none focus-visible:border-none focus-visible:ring-0 md:text-sm",
            leading && "uppercase",
          )}
        />
      </ETableCell>
      <ETableCell className="w-8 p-0">
        <GroupingActionsDropdown
          grouping={grouping}
          // the other grouping is only added within the leading one
          fields={leading ? [field === "group" ? "area" : "group"] : []}
          removable={field}
        />
      </ETableCell>
    </ETableRow>
  );
}

/**
 * EmptyPositionsRow is shown in a table without positions. It offers to
 * add the first position or grouping.
 */
function EmptyPositionsRow({ fields }: { fields: ("group" | "area")[] }) {
  const { columns, addPositionOptions, disabled } =
    usePositionComponentContext();
  const addPosition = useAddPosition();
  const groupName = useGroupingName("group");
  const areaName = useGroupingName("area");
  const [adding, setAdding] = useState<"group" | "area">();

  return (
    <ETableRow>
      <ETableCell colSpan={columns.length}>
        <div className="flex flex-wrap items-center justify-center gap-2 py-2">
          <span className="text-muted-foreground mr-2 text-sm">
            {t("Noch keine Positionen")}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={disabled}>
                <Plus />
                {t("Position hinzufügen")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              {addPositionOptions.map((option) => (
                <DropdownMenuItem
                  key={option.label ?? option.text}
                  onSelect={() => addPosition(option, {})}
                >
                  <Plus />
                  {option.label ?? option.text}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {fields.map((field) => (
            <Button
              key={field}
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={() => setAdding(field)}
            >
              <Plus />
              {`${field === "group" ? groupName : areaName} ${t("hinzufügen")}`}
            </Button>
          ))}
        </div>
        <AddGroupingDialog
          field={adding}
          grouping={{ group: "", area: "" }}
          onClose={() => setAdding(undefined)}
        />
      </ETableCell>
    </ETableRow>
  );
}

/**
 * GroupingActionsDropdown adds positions to the grouping, and the fields
 * as groupings within it. With removable, it removes the grouping, i.e.
 * all positions selected by its field.
 */
function GroupingActionsDropdown({
  grouping,
  fields,
  removable,
}: {
  grouping: Grouping;
  fields: ("group" | "area")[];
  removable?: "group" | "area";
}) {
  const { addPositionOptions, disabled } = usePositionComponentContext();
  const addPosition = useAddPosition();
  const groupName = useGroupingName("group");
  const areaName = useGroupingName("area");
  const [adding, setAdding] = useState<"group" | "area">();
  const [removing, setRemoving] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button variant="ghost" className="w-8 rounded-none">
            {removable ? <EllipsisVertical /> : <Plus />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuGroup>
            <DropdownMenuLabel>{t("Position hinzufügen")}</DropdownMenuLabel>
            {addPositionOptions.map((option) => (
              <DropdownMenuItem
                key={option.label ?? option.text}
                onSelect={() => addPosition(option, grouping)}
              >
                <Plus />
                {option.label ?? option.text}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
          {fields.length > 0 && (
            <>
              <DropdownMenuSeparator />
              {fields.map((field) => (
                <DropdownMenuItem key={field} onSelect={() => setAdding(field)}>
                  <Plus />
                  {`${field === "group" ? groupName : areaName} ${t("hinzufügen")}`}
                </DropdownMenuItem>
              ))}
            </>
          )}
          {removable && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setRemoving(true)}>
                <Trash2 />
                {`${removable === "group" ? groupName : areaName} ${t("löschen")}`}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <AddGroupingDialog
        field={adding}
        grouping={grouping}
        onClose={() => setAdding(undefined)}
      />
      {removable && (
        <RemoveGroupingDialog
          open={removing}
          onOpenChange={setRemoving}
          grouping={grouping}
          field={removable}
        />
      )}
    </>
  );
}

/**
 * useGroupingName returns the configured name of the field, e.g.
 * "Gebäude" for the group.
 */
function useGroupingName(field: "group" | "area"): string {
  const { tabs, tabsName, sectionsName } = usePositionComponentContext();
  return field === tabs ? tabsName : sectionsName;
}

/**
 * AddGroupingDialog adds the title of a new grouping of field within the
 * grouping, e.g. a room within a building. It is open while field is set.
 */
function AddGroupingDialog({
  field,
  grouping,
  onClose,
}: {
  field?: "group" | "area";
  grouping: Grouping;
  onClose: () => void;
}) {
  const { document, addPositionMutation, fieldNamePrefix } =
    usePositionComponentContext();
  const [post, { isLoading, reset }] = addPositionMutation;
  const { write } = useErrValidationFailedContext();
  const [positionId, setPositionId] = useState(v4());
  // this is not the "text" of the title but the name of the grouping
  const [name, setName] = useState("");
  const label = useGroupingName(field ?? "group");

  const doAdd = () => {
    if (isLoading || !field) return;

    post({
      id: document.id,
      positionId,
      kind:
        field === "group" ? PositionKind.GroupTitle : PositionKind.AreaTitle,
      ...grouping,
      [field]: name,
    })
      .unwrap()
      .then(() => {
        setName("");
        setPositionId(v4());
        reset();
        onClose();
      })
      .catch((e) => {
        write(e);
      });
  };

  return (
    <Dialog open={!!field} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${label} ${t("hinzufügen")}`}</DialogTitle>
        </DialogHeader>
        <TextInputField
          label={t("Name")}
          text={name}
          onTextChange={setName}
          placeholder={t("Name eingeben ...")}
          fieldName={`${fieldNamePrefix}positions.${positionId}.${field}`}
        />
        <CommonDialogFooter isLoading={isLoading} onClickAction={doAdd} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * RemoveGroupingDialog removes all positions of the grouping selected by
 * field, e.g. a building with all its rooms.
 */
function RemoveGroupingDialog({
  open,
  onOpenChange,
  grouping,
  field,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  grouping: Grouping;
  field: "group" | "area";
}) {
  const { document, removePositionsByGroupingMutation } =
    usePositionComponentContext();
  const [del, { isLoading, error, reset }] = removePositionsByGroupingMutation;
  const label = useGroupingName(field);

  const doDelete = () => {
    if (isLoading) return;

    del({ id: document.id, ...grouping })
      .unwrap()
      .then(() => {
        reset();
        onOpenChange(false);
      })
      .catch((e) => {
        toast(...parseRTKQueryErrorToSonner(e));
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${label} ${t("löschen?")}`}</DialogTitle>
          <DialogDescription>
            {`${t("Alle Positionen in")} ${grouping[field]} ${t("werden unwiderruflich gelöscht.")}`}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doDelete}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}

// lockedColumns are always shown
const lockedColumns: TableColumn[] = [
  TableColumn.DnDGrip,
  TableColumn.Number,
  TableColumn.Active,
  TableColumn.Text,
  TableColumn.FinalPrice,
  TableColumn.Actions,
];

/**
 * ColumnSettingsDropdown lets users choose the columns shown, apart from
 * the locked ones.
 */
function ColumnSettingsDropdown() {
  const { document, selectedColumns, setColumns } =
    usePositionComponentContext();

  const configurable = TableColumsList.filter(
    (column) =>
      !lockedColumns.includes(column) &&
      (column !== TableColumn.ServiceObject ||
        (document.serviceObjects?.length ?? 0) > 1),
  );

  const toggle = (column: TableColumn, shown: boolean) => {
    const selected = shown
      ? [...selectedColumns, column]
      : selectedColumns.filter((c) => c !== column);
    setColumns(TableColumsList.filter((c) => selected.includes(c)));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-8 rounded-none"
          aria-label={t("Spalten")}
          title={t("Spalten")}
        >
          <Settings2 />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>{t("Spalten")}</DropdownMenuLabel>
        {configurable.map((column) => (
          <DropdownMenuCheckboxItem
            key={column.key}
            checked={selectedColumns.includes(column)}
            onCheckedChange={(shown) => toggle(column, shown)}
            // keep the menu open to toggle several columns
            onSelect={(e) => e.preventDefault()}
          >
            {column.title}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
